import { QueryClient } from '@tanstack/react-query'
import {
  couponDetailInfoTabQueryOption,
  giftedCouponDetailQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import {
  CouponDetailInfoTabSpace,
  GiftedCouponDetailSpace,
  GiftSendCancelSpace,
} from 'src/types/api'
import { handleJsonParse, maskPhoneNumber } from 'src/utils'
import { DateUtils } from 'src/utils/date'
import { useModal } from 'src/provider/ModalContextProvider'
import {
  giftSendCancelQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import React, { useCallback } from 'react'
import {
  ERRCODE_SUCCESS,
  ERRCODES_COMMON,
  ERRCODES_PURCHASE_CANCEL,
} from 'src/service/constant/errcodes'
import usePageNav from 'src/hooks/usePageNav.hook'
import { useToast } from 'src/provider/ToastContextProvider'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import GiftedCouponDetailPageView from 'src/pages/GiftedCouponPage/GiftedCouponDetailPage/view'
import { Params } from 'react-router-dom'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { Bridge } from 'src/utils/bridge'

export interface GiftedCouponDetailPageProps {
  giftedCouponDetailData?: GiftedCouponDetailSpace.Response
  isSending: boolean
  isSendComplete: boolean
  isCancelAble: boolean
  giftedCouponDetailDataList: ({
    label: string
    value: { text: string; bold?: boolean; color?: string }[]
  } | null)[]
  cancelSendGiftHandler: () => void
  reShareUrlHandler: (url: string) => void
  couponDetailTabData?: CouponDetailInfoTabSpace.Response
  Introduction?: string
}

export const giftedCouponDetailPageLoader =
  (queryClient: QueryClient) =>
  async ({ params: { giftIdx } }: { params: Params<keyof MatchParams> }) => {
    return Promise.allSettled([
      await queryClient.ensureQueryData(
        giftedCouponDetailQueryOption({ GiftIdx: Number(giftIdx) }),
      ),
    ])
  }

const GiftedCouponDetailPage = () => {
  const { giftIdx } = useParamsType()
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { DateFormat, isValidInDays, isValidInYears, getAfter7Days } = DateUtils
  const { qnaPageNav, refundPageNav } = usePageNav()
  const { web } = useChannel()

  const {
    data: giftedCouponDetailData,
    isLoading: isGiftedCouponDetailLoading,
    refetch: giftedCouponDetailRefetch,
    isRefetching: isGiftedCouponDetailRefetching,
  } = useCustomQuery<GiftedCouponDetailSpace.Response>(
    giftedCouponDetailQueryOption({ GiftIdx: Number(giftIdx) }, true),
  )

  const {
    CouponIdx,
    CouponOrderID,
    CouponNumber,
    CouponType,
    PinNumber,
    GiftStatus,
    GiftType,
    RegularPrice,
    SalePrice,
    ExpireDT,
    PurchaseDT,
    RegDT,
    RecipientPNumber,
    PurchaseIdx,
    ValidDay,
  } = giftedCouponDetailData?.Body as GiftedCouponDetailSpace.Response

  const {
    data: couponDetailInfoTabQueryData,
    isSuccess: isCouponDetailInfoSuccess,
    isLoading: isCouponDetailInfoLoading,
  } = useCustomQuery<CouponDetailInfoTabSpace.Response>(
    couponDetailInfoTabQueryOption({
      CouponType: Number(CouponType) as 0 | 1,
      CouponNumber: CouponNumber,
    }),
  )

  const { mutateAsync: giftSendCancelMutate, isPending: isGiftSendCancelPending } =
    useCustomMutation<GiftSendCancelSpace.Request, GiftSendCancelSpace.Response>(
      giftSendCancelQueryOption(),
    )

  const setCouponDetailInfoItems = useCallback(() => {
    return {
      ...couponDetailInfoTabQueryData?.Body,
      CouponInfo: {
        Title: couponDetailInfoTabQueryData?.Body?.CouponInfo?.Title,
        Contents: couponDetailInfoTabQueryData?.Body?.CouponInfo?.Contents?.replace(
          '{{ValidDay}}',
          `${ValidDay}일`,
        ),
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCouponDetailInfoSuccess, couponDetailInfoTabQueryData])

  const isSending = GiftStatus === 0
  const isSendComplete = GiftStatus === 1
  // const isHotDeal = CouponType === 1
  const isCancelAble = isSending && isValidInYears(PurchaseDT, 5)
  const isSendAfterPurchase = GiftType === 1

  const giftedCouponDetailDataList = [
    {
      label: '선물 상태',
      value: isSending
        ? [
            { text: '선물 받는 중 ', bold: true },
            {
              text: `(~${getAfter7Days(RegDT).format('YY.MM.DD')})`,
              color: 'var(--black-50)',
            },
          ]
        : [{ text: isSendComplete ? '수신 완료' : '수신 거절' }],
    },
    ...(PinNumber?.trim() ? [{ label: '쿠폰 인증번호', value: [{ text: PinNumber.trim() }] }] : []),
    { label: '유효 기간', value: [{ text: DateFormat(ExpireDT, 'YY.MM.DD') }] },
    { label: '주문 번호', value: [{ text: CouponOrderID }] },
    { label: '구매 날짜', value: [{ text: DateFormat(PurchaseDT, 'YY.MM.DD') }] },
    { label: '선물 날짜', value: [{ text: DateFormat(RegDT, 'YY.MM.DD') }] },
    { label: '상품 금액', value: [{ text: `${RegularPrice?.toLocaleString()}원` }] },
    { label: '결제 금액', value: [{ text: `${SalePrice?.toLocaleString()}원` }] },
    {
      label: '받은 사람',
      value: [
        {
          text: isSending
            ? '수락 대기 중'
            : isSendComplete
              ? maskPhoneNumber(RecipientPNumber)
              : '수신 거절',
        },
      ],
    },
  ]

  const giftCancelSubmit = () => {
    giftSendCancelMutate({ GiftIdx: Number(giftIdx) }).then(({ Header }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        giftedCouponDetailRefetch?.().then(() => {
          openToast({ contents: '선물이 취소되었습니다.' })
          window.scrollTo({ top: 0 })
        })
      } else {
        const ErrMsg = handleJsonParse(Header.ErrMsg)
        const { Title = '', Contents = '' } = ErrMsg
        const handleRefundPageNav = () =>
          refundPageNav(Number(PurchaseIdx), Number(CouponIdx), CouponNumber, CouponOrderID, RegDT)

        switch (Header.ErrCode) {
          case ERRCODES_PURCHASE_CANCEL.ERROR_ALREADY_USE:
            openModal({
              contents: { t: Title, d: Contents },
              button: { cancel: { t: '닫기' } },
            })
            break
          case ERRCODES_PURCHASE_CANCEL.ERROR_CANCEL_PERIOD_OVER:
            openModal({
              contents: { t: Title, d: Contents },
              button: {
                submit: { t: '환불 신청', f: handleRefundPageNav },
                cancel: { t: '닫기' },
              },
            })
            break
          case ERRCODES_PURCHASE_CANCEL.ERROR_CANCEL_ERROR:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '문의하기', f: qnaPageNav }, cancel: { t: '닫기' } },
            })
            break
          //통신 모듈에 있는 에러 핸들링 사용
          case ERRCODES_COMMON.PIN_CHECK_ERROR:
          case ERRCODES_COMMON.SESSION_ERROR:
          case ERRCODES_COMMON.ERROR_LOGOUT:
            break
          default:
            openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
            break
        }
      }
    })
  }

  const cancelSendGiftHandler = () => {
    openModal({
      contents: isSendAfterPurchase
        ? {
            t: '선물이 취소됩니다.',
            d: '선물을 취소하는 경우, 해당 쿠폰은 구매내역으로 복원됩니다.',
          }
        : {
            t: '구매가 취소됩니다.',
            d: '해당 선물을 취소하는 경우, 쿠폰 구매가 취소됩니다.',
          },
      button: { submit: { t: '선물 취소', f: giftCancelSubmit }, cancel: { t: '닫기' } },
    })
  }

  const reShareUrlHandler = async (url: string) => {
    if (web) {
      try {
        await navigator.share({ url: url })
      } catch (error) {
        await navigator.clipboard.write([new ClipboardItem({ 'text/plain': url })])
        openToast({ contents: 'url이 클립보드에 복사되었습니다.' })
      }
    } else {
      Bridge.handleReqSystemShare(url)
    }
  }

  const props: GiftedCouponDetailPageProps = {
    giftedCouponDetailData: giftedCouponDetailData?.Body,
    couponDetailTabData: setCouponDetailInfoItems(),
    cancelSendGiftHandler,
    reShareUrlHandler,
    giftedCouponDetailDataList,
    isSending,
    isSendComplete,
    isCancelAble,
    Introduction: giftedCouponDetailData?.Body?.Introduction,
  }

  const renderLoading =
    isCouponDetailInfoLoading ||
    isGiftedCouponDetailLoading ||
    isGiftedCouponDetailRefetching ||
    isGiftSendCancelPending

  return (
    <React.Fragment>
      <RenderIf when={renderLoading}>
        <Loading.Default />
      </RenderIf>
      <GiftedCouponDetailPageView {...props} />
    </React.Fragment>
  )
}
export default GiftedCouponDetailPage
